import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';
import type { Tables } from '@/integrations/supabase/types';

// Server-side deduplication: unique index `match_team_invitations_match_id_invitee_team_id_key`
// on (match_id, invitee_team_id) enforces uniqueness across all devices/sessions.
// Duplicate inserts return Postgres error code 23505, handled in onError below.



export interface MatchTeamInvitation {
  id: string;
  match_id: string;
  inviter_team_id: string;
  invitee_team_id: string;
  invited_by: string;
  status: 'pending' | 'accepted' | 'rejected';
  message: string | null;
  created_at: string;
  responded_at: string | null;
  responded_by: string | null;
  inviter_team?: {
    id: string;
    name: string;
    avatar: string | null;
    sport: string;
  };
  invitee_team?: {
    id: string;
    name: string;
    avatar: string | null;
    sport: string;
  };
  match?: Tables<'matches'>;
  invited_by_profile?: Tables<'profiles'>;
}

// Get invitations for a specific match
export function useMatchTeamInvitations(matchId?: string) {
  return useQuery({
    queryKey: ['match-team-invitations', matchId],
    queryFn: async () => {
      if (!matchId) return [];

      const { data, error } = await supabase
        .from('match_team_invitations')
        .select(`
          *,
          inviter_team:teams!match_team_invitations_inviter_team_id_fkey(id, name, avatar, sport),
          invitee_team:teams!match_team_invitations_invitee_team_id_fkey(id, name, avatar, sport),
          invited_by_profile:profiles!match_team_invitations_invited_by_fkey(id, name, avatar)
        `)
        .eq('match_id', matchId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as MatchTeamInvitation[];
    },
    enabled: !!matchId,
  });
}

// Get pending invitations for teams where user is admin
export function useMyTeamPendingInvitations() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['my-team-pending-invitations', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      // Get teams where user is admin
      const { data: adminTeams, error: teamError } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('profile_id', profile.id)
        .eq('role', 'admin');

      if (teamError) throw teamError;
      if (!adminTeams?.length) return [];

      const teamIds = adminTeams.map(t => t.team_id);

      // Get pending invitations for these teams
      const { data, error } = await supabase
        .from('match_team_invitations')
        .select(`
          *,
          inviter_team:teams!match_team_invitations_inviter_team_id_fkey(id, name, avatar, sport),
          invitee_team:teams!match_team_invitations_invitee_team_id_fkey(id, name, avatar, sport),
          match:matches(*),
          invited_by_profile:profiles!match_team_invitations_invited_by_fkey(id, name, avatar)
        `)
        .in('invitee_team_id', teamIds)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as MatchTeamInvitation[];
    },
    enabled: !!profile?.id,
  });
}

// Send team invitation
export function useSendTeamInvitation() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      matchId,
      inviterTeamId,
      inviteeTeamId,
      message,
    }: {
      matchId: string;
      inviterTeamId: string;
      inviteeTeamId: string;
      message?: string;
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      // No client-side lock: dedup is enforced server-side by the unique index
      // on (match_id, invitee_team_id). Duplicate inserts fail with 23505.
      const { data, error } = await supabase
        .from('match_team_invitations')
        .insert({
          match_id: matchId,
          inviter_team_id: inviterTeamId,
          invitee_team_id: inviteeTeamId,
          invited_by: profile.id,
          message: message || null,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error('Đội này đã được mời');
        }
        throw error;
      }
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match-team-invitations', variables.matchId] });
      toast.success('Đã gửi lời mời đến đội');
    },
    onError: (error: any) => {
      toast.error('Lỗi gửi lời mời', { description: error?.message });
    },

  });
}

// Respond to team invitation (accept/reject)
export function useRespondToTeamInvitation() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({
      invitationId,
      accept,
    }: {
      invitationId: string;
      accept: boolean;
    }) => {
      if (!profile?.id) throw new Error('Chưa đăng nhập');

      // Get invitation details first
      const { data: invitation, error: fetchError } = await supabase
        .from('match_team_invitations')
        .select(`
          *,
          invitee_team:teams!match_team_invitations_invitee_team_id_fkey(
            id,
            members:team_members(profile_id)
          ),
          match:matches(id, is_ranked, sport)
        `)
        .eq('id', invitationId)
        .single();

      if (fetchError) throw fetchError;
      if (!invitation) throw new Error('Không tìm thấy lời mời');

      // If accepting, validate team members
      if (accept) {
        const teamMembers = (invitation.invitee_team as any)?.members || [];
        const match = (invitation as any).match;

        // Check if any team member is banned
        const banChecks = await Promise.all(
          teamMembers.map(async (m: { profile_id: string }) => {
            const { data: isBanned } = await supabase.rpc('is_player_banned', {
              _profile_id: m.profile_id,
            });
            return { profileId: m.profile_id, isBanned };
          })
        );

        const bannedMembers = banChecks.filter((check) => check.isBanned);
        if (bannedMembers.length > 0) {
          throw new Error('Không thể chấp nhận: Đội có thành viên đang bị cấm thi đấu');
        }

        // If ranked match, validate ranked_sport for all team members
        if (match?.is_ranked) {
          const memberProfileIds = teamMembers.map((m: { profile_id: string }) => m.profile_id);
          
          // NOTE: RLS trên `profiles` chỉ cho đọc của chính user.
          // Dùng view `profiles_public` để đọc ranked_sport của các thành viên khác.
          const { data: memberProfiles, error: profileError } = await supabase
            .from('profiles_public')
            .select('id, ranked_sport')
            .in('id', memberProfileIds);

          if (profileError) throw profileError;

          const invalidMembers = memberProfiles?.filter(
            (p) => p.ranked_sport !== match.sport
          );

          if (invalidMembers && invalidMembers.length > 0) {
            throw new Error(
              `Trận đấu rank: Một số thành viên chưa đăng ký môn ${match.sport} làm ranked sport`
            );
          }
        }
      }

      // Update invitation status
      const { error: updateError } = await supabase
        .from('match_team_invitations')
        .update({
          status: accept ? 'accepted' : 'rejected',
          responded_at: new Date().toISOString(),
          responded_by: profile.id,
        })
        .eq('id', invitationId);

      if (updateError) throw updateError;

      // If accepted, set team_b_id and add team members
      if (accept) {
        // Update match with team_b_id
        const { error: matchError } = await supabase
          .from('matches')
          .update({ team_b_id: invitation.invitee_team_id })
          .eq('id', invitation.match_id);

        if (matchError) throw matchError;

        // Add all team members as participants (team 2)
        const teamMembers = (invitation.invitee_team as any)?.members || [];
        if (teamMembers.length > 0) {
          const participants = teamMembers.map((m: { profile_id: string }) => ({
            match_id: invitation.match_id,
            profile_id: m.profile_id,
            team: 2,
          }));

          const { error: participantError } = await supabase
            .from('match_participants')
            .upsert(participants, { onConflict: 'match_id,profile_id' });

          if (participantError) throw participantError;
        }

        // Reject other pending invitations for this match
        await supabase
          .from('match_team_invitations')
          .update({ 
            status: 'rejected',
            responded_at: new Date().toISOString(),
          })
          .eq('match_id', invitation.match_id)
          .eq('status', 'pending')
          .neq('id', invitationId);
      }

      return invitation;
    },
    onSuccess: (invitation, variables) => {
      queryClient.invalidateQueries({ queryKey: ['match-team-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['my-team-pending-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['match', invitation.match_id] });
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      
      if (variables.accept) {
        toast.success('Đã chấp nhận lời mời thi đấu');
      } else {
        toast.info('Đã từ chối lời mời thi đấu');
      }
    },
    onError: (error: Error) => {
      toast.error('Lỗi xử lý lời mời', { description: error.message });
    },
  });
}
